import { useState, useEffect, useRef, useCallback } from 'react';
import proxy from '../api/axios';
import { fetchQuery, invalidateQuery } from '../api/queryCache';

const submissionKey = (problemId, contestId) =>
  `submissions:${problemId}:${contestId || 'practice'}`;

const SUBMISSIONS_STALE = 2 * 60 * 1000;

/**
 * Lazy-loads submissions when the Submission tab is active.
 * Uses shared client cache to avoid refetching on revisit within stale window.
 */
export function useProblemSubmissions({
  problemId,
  contestId = null,
  isLoggedIn,
  activeTab,
  submissionTabIndex = 2,
} = {}) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const cacheKey = problemId ? submissionKey(problemId, contestId) : null;

  const fetchSubmissions = useCallback(
    async ({ force = false } = {}) => {
      if (!isLoggedIn || !problemId || !cacheKey) {
        setSubmissions([]);
        setError(null);
        setLoading(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);

      try {
        const data = await fetchQuery(
          cacheKey,
          async () => {
            const url = contestId
              ? `/problems/${problemId}/${contestId}/submissions`
              : `/problems/${problemId}/submissions`;
            const { data: res } = await proxy.get(url);
            return Array.isArray(res) ? res : [];
          },
          { staleTime: SUBMISSIONS_STALE, force },
        );
        if (requestId !== requestIdRef.current) return;
        setSubmissions(data);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setSubmissions([]);
        setError(
          err?.response?.data?.message || 'Could not load submissions. Try again.',
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [problemId, contestId, isLoggedIn, cacheKey],
  );

  const refresh = useCallback(() => {
    if (cacheKey) invalidateQuery(cacheKey);
    return fetchSubmissions({ force: true });
  }, [fetchSubmissions, cacheKey]);

  useEffect(() => {
    if (activeTab !== submissionTabIndex || !isLoggedIn || !problemId) return;
    fetchSubmissions();
  }, [activeTab, submissionTabIndex, isLoggedIn, problemId, contestId, fetchSubmissions]);

  useEffect(() => {
    requestIdRef.current += 1;
    setSubmissions([]);
    setError(null);
    setLoading(false);
  }, [problemId, contestId, isLoggedIn]);

  return { submissions, loading, error, refresh };
}

export async function fetchSubmissionDetail(submissionId) {
  const { data } = await proxy.get(`/problems/submission/${submissionId}`);
  return data;
}
